import { Popup } from "@mobiscroll/react";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { RootState } from "../../../../app/rootReducer";
import { useSelector } from "../../../../app/helpers";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import { FleetManagementRow } from "../../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../../types/FleetManagement.types";

export type FleetCalendarEventTooltipContents = {
  startTime: string;
  endTime: string;
  userInfo: {
    userId: string;
    firstName: string;
    surname: string;
    email: string;
  };
  bookingId: number;
  isCheckedIn: boolean;
  isCheckedOut: boolean;
  reason: string | null;
  location: {
    id: number;
    name: string;
  };
  vehicleInventoryId: number;
  companyId: number;
  /** User ID who created the booking (when different from userInfo.userId, e.g. book-on-behalf). */
  userBookedBy?: string;
};

/** Tooltip contents: booking (has startTime) or blocking event (has start + vehicleInventoryId). */
export type FleetEventTooltipContentsUnion =
  | FleetCalendarEventTooltipContents
  | VehicleBlockingEvent
  | null;

function isBookingContents(
  c: FleetEventTooltipContentsUnion
): c is FleetCalendarEventTooltipContents {
  return c != null && "startTime" in c;
}

type BookingCheckInOutChip = {
  label: string;
  color: "success" | "default";
  variant: "filled" | "outlined";
};

export function getBookingCheckInOutChips(
  isCheckedIn: boolean,
  isCheckedOut: boolean,
  t: (key: string) => string
): BookingCheckInOutChip[] {
  if (isCheckedOut) {
    return [{ label: t("CheckedOut"), color: "success", variant: "filled" }];
  }

  if (isCheckedIn) {
    return [
      { label: t("Checked in"), color: "success", variant: "filled" },
      { label: t("NotCheckedOut"), color: "default", variant: "outlined" }
    ];
  }

  return [{ label: t("NotCheckedIn"), color: "default", variant: "outlined" }];
}

function BookingCheckInOutChips({
  isCheckedIn,
  isCheckedOut,
  t
}: Readonly<{
  isCheckedIn: boolean;
  isCheckedOut: boolean;
  t: (key: string) => string;
}>) {
  const chips = getBookingCheckInOutChips(isCheckedIn, isCheckedOut, t);

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
      {chips.map(({ label, color, variant }) => (
        <Chip key={label} size="small" label={label} color={color} variant={variant} />
      ))}
    </Box>
  );
}

export type ColleagueForTooltip = {
  userId: string;
  firstName: string;
  surname: string;
  email?: string;
};

export type FleetEventTooltipContentsProps = {
  contents: FleetEventTooltipContentsUnion;
  fleetData: FleetManagementRow[] | undefined;
  /** Optional list of users to resolve userBookedBy ID to display name in booking tooltip. */
  allUsers?: ColleagueForTooltip[];
  tooltipAnchor: HTMLElement | undefined;
  isTooltipOpen: boolean;
  handleTooltipClose: () => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
};

export function FleetEventTooltipContents({
  contents,
  fleetData,
  allUsers,
  tooltipAnchor,
  isTooltipOpen,
  handleTooltipClose,
  handleMouseEnter,
  handleMouseLeave
}: Readonly<FleetEventTooltipContentsProps>) {
  const { t } = useTranslation();
  const {
    settings: { timezone: userTimezone }
  } = useSelector((state: RootState) => state.login);

  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = `${dateFormat} ${timeFormat}`;
  const timezone = userTimezone;

  const formatDateTime = (iso: string) => {
    const dt = DateTime.fromISO(iso).setZone(timezone);
    return dt.isValid ? dt.toFormat(localeFullFormat) : iso;
  };

  if (!contents) return null;

  return (
    <Popup
      anchor={tooltipAnchor}
      contentPadding={false}
      display="anchored"
      isOpen={isTooltipOpen}
      scrollLock={false}
      showOverlay={false}
      touchUi={false}
      width={350}
      onClose={handleTooltipClose}
    >
      <Box
        className="mds-tooltip"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          p: 2,
          maxWidth: 350
        }}
      >
        {isBookingContents(contents) ? (
          <BookingTooltipBody
            contents={contents}
            formatDateTime={formatDateTime}
            fleetData={fleetData}
            allUsers={allUsers}
            t={t}
          />
        ) : (
          <BlockingTooltipBody
            contents={contents}
            formatDateTime={formatDateTime}
            fleetData={fleetData}
            t={t}
          />
        )}
      </Box>
    </Popup>
  );
}

export function getBookedByDisplayName(
  userBookedById: string | undefined,
  allUsers: ColleagueForTooltip[] | undefined
): string | null {
  if (!userBookedById || !allUsers?.length) return null;
  const u = allUsers.find(x => x.userId === userBookedById);
  if (!u) return null;
  const first = u.firstName?.trim() ?? "";
  const last = u.surname?.trim() ?? "";
  return [first, last].filter(Boolean).join(" ") || u.email || userBookedById;
}

function BookingTooltipBody({
  contents,
  formatDateTime,
  fleetData,
  allUsers,
  t
}: Readonly<{
  contents: FleetCalendarEventTooltipContents;
  formatDateTime: (iso: string) => string;
  fleetData: FleetManagementRow[] | undefined;
  allUsers?: ColleagueForTooltip[];
  t: (key: string) => string;
}>) {
  const {
    startTime,
    endTime,
    userInfo,
    bookingId,
    isCheckedIn,
    isCheckedOut,
    reason,
    location,
    vehicleInventoryId,
    userBookedBy
  } = contents;

  const bookedByDisplay =
    userBookedBy &&
    userBookedBy !== userInfo.userId &&
    getBookedByDisplayName(userBookedBy, allUsers);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.5
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {userInfo.firstName} {userInfo.surname}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {fleetData?.find(row => row.id === vehicleInventoryId)?.name ?? `#${bookingId}`}
        </Typography>
      </Box>

      {userInfo.email && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {userInfo.email}
        </Typography>
      )}

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t("Time")}
        </Typography>
        <Typography variant="body2">
          {formatDateTime(startTime)} – {formatDateTime(endTime)}
        </Typography>
      </Box>

      {bookedByDisplay && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            {t("_fleetEventDetail_bookedBy")}: {bookedByDisplay}
          </Typography>
        </Box>
      )}

      {location?.name && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("Location")}
          </Typography>
          <Typography variant="body2">{location.name}</Typography>
        </Box>
      )}

      {reason && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("FleetEventReason")}
          </Typography>
          <Typography variant="body2">{reason}</Typography>
        </Box>
      )}

      <BookingCheckInOutChips isCheckedIn={isCheckedIn} isCheckedOut={isCheckedOut} t={t} />
    </>
  );
}

function BlockingTooltipBody({
  contents,
  formatDateTime,
  fleetData,
  t
}: Readonly<{
  contents: VehicleBlockingEvent;
  formatDateTime: (iso: string) => string;
  fleetData: FleetManagementRow[] | undefined;
  t: (key: string) => string;
}>) {
  const { start, end, reason, vehicleInventoryId, createdByUser } = contents;
  const vehicleName = fleetData?.find(row => row.id === vehicleInventoryId)?.name;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.5
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {t("FleetEventBlocked")}
        </Typography>
        {vehicleName && (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {vehicleName}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t("Time")}
        </Typography>
        <Typography variant="body2">
          {formatDateTime(start)} – {formatDateTime(end)}
        </Typography>
      </Box>

      {createdByUser && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("Blocked by")}
          </Typography>
          <Typography variant="body2">
            {createdByUser?.firstName} {createdByUser?.surname}
          </Typography>
        </Box>
      )}

      {reason && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("FleetEventReason")}
          </Typography>
          <Typography variant="body2">{reason}</Typography>
        </Box>
      )}
    </>
  );
}
